// Cave84 — Settings Page (Phase 7)
// Owner-only. Sections: Profile, Access/Viewers, Backup, Batch Operations, About.
// Spec §4.11 F11 + §6.3 Screen 8.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import type { User } from '@cave84/shared-types';
import { db } from '@/services/firebase';
import { useAuthStore } from '@/stores/auth.store';
import { useInventory, DEFAULT_FILTERS } from '@/hooks/useInventory';
import type { InventoryRow } from '@/hooks/useInventory';
import {
  useCustomWineRegions,
  useAddCustomWineRegion,
  useDeleteCustomWineRegion,
  useSlugify,
  type CustomWineRegion,
} from '@/hooks/useCustomWineRegions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALL_FILTERS = {
  ...DEFAULT_FILTERS,
  availableAtHome: false,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-surface-1 border border-border-medium rounded-lg overflow-hidden mb-5">
      <div className="px-4 py-3 border-b border-border-subtle">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      </div>
      <div className="px-4 py-4">{children}</div>
    </section>
  );
}

function SaveBtn({
  saving,
  saved,
  onClick,
}: {
  saving: boolean;
  saved: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className="px-4 py-1.5 rounded-md bg-gold text-base text-sm font-medium hover:bg-gold/90 disabled:opacity-50 transition-colors"
    >
      {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Profile section
// ---------------------------------------------------------------------------

function ProfileSection({ user }: { user: User }) {
  const setUserDoc = useAuthStore((s) => s.setUserDoc);
  const [displayName, setDisplayName] = useState(user.display_name);
  const [langPref, setLangPref] = useState<'en' | 'he'>(user.language_pref ?? 'en');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.id), {
        display_name: displayName.trim() || user.display_name,
        language_pref: langPref,
      });
      setUserDoc({ ...user, display_name: displayName.trim() || user.display_name, language_pref: langPref });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Profile">
      <div className="space-y-4">
        <div>
          <span className="block text-xs text-text-muted mb-1">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setSaved(false); }}
            className="w-full max-w-xs px-3 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
          />
        </div>
        <div>
          <span className="block text-xs text-text-muted mb-1">UI language</span>
          <div className="flex gap-3">
            {(['en', 'he'] as const).map((lang) => (
              <label key={lang} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="langPref"
                  value={lang}
                  checked={langPref === lang}
                  onChange={() => { setLangPref(lang); setSaved(false); }}
                  className="accent-gold"
                />
                <span className="text-sm text-text-secondary">{lang === 'en' ? 'English' : 'עברית'}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs text-text-muted mb-1">Email</p>
          <p className="text-sm text-text-secondary mono">{user.email}</p>
        </div>
        <div>
          <p className="text-xs text-text-muted mb-1">Role</p>
          <p className="text-sm text-gold font-medium">Owner</p>
        </div>
        <SaveBtn saving={saving} saved={saved} onClick={() => void handleSave()} />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Access section — show cellar URL + current viewers
// ---------------------------------------------------------------------------

function AccessSection() {
  const [guests, setGuests] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const cellarUrl = 'https://wine.bar84.com';

  useEffect(() => {
    void (async () => {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'viewer'));
        const snap = await getDocs(q);
        setGuests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<User, 'id'>) })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Section title="Access">
      <div className="space-y-4">
        <div>
          <p className="text-xs text-text-muted mb-1">Cellar URL</p>
          <div className="flex items-center gap-2">
            <code className="text-sm text-text-secondary bg-surface-0 border border-border-subtle rounded px-2 py-1">
              {cellarUrl}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(cellarUrl)}
              className="text-xs text-text-muted hover:text-gold transition-colors border border-border-subtle rounded px-2 py-1"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Share this URL with guests. Anyone who signs in with "Continue as Guest" gets
            read-only Guest access automatically — no purchase prices, no cellar totals,
            no editing.
          </p>
        </div>

        <div>
          <p className="text-xs text-text-muted mb-2">Current guests</p>
          {loading ? (
            <p className="text-xs text-text-muted animate-pulse">Loading…</p>
          ) : guests.length === 0 ? (
            <p className="text-xs text-text-muted">No guests yet.</p>
          ) : (
            <ul className="space-y-1">
              {guests.map((v) => (
                <li key={v.id} className="flex items-center gap-2 text-sm text-text-secondary">
                  <span className="w-2 h-2 rounded-full bg-cellar-virtual flex-shrink-0" />
                  <span>{v.display_name}</span>
                  <span className="text-text-muted text-xs mono">{v.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Link
          to="/settings/guest-log"
          className="inline-flex items-center gap-2 text-xs text-text-muted hover:text-gold transition-colors border border-border-subtle rounded px-3 py-1.5"
        >
          View Guest Login Log →
        </Link>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Backup section
// ---------------------------------------------------------------------------

function serializeRows(rows: InventoryRow[]): string {
  const payload = rows.map((r) => ({
    bottle_id: r.bottle.id,
    producer: r.producer.name,
    cuvee: r.wine.cuvee_name,
    vintage: r.vintage.year,
    style: r.wine.style,
    format: r.bottle.format,
    status: r.bottle.status,
    cellar: r.cellar.name,
    cost_eur: r.bottle.cost_eur_at_purchase,
    purchase_date: r.bottle.purchase_date ?? null,
    notes: r.bottle.notes ?? null,
    drink_from: r.vintage.drinking_window_current?.from_year ?? null,
    drink_to: r.vintage.drinking_window_current?.to_year ?? null,
  }));
  return JSON.stringify({ exported_at: new Date().toISOString(), count: payload.length, bottles: payload }, null, 2);
}

function BackupSection() {
  const { rows, isLoading } = useInventory(ALL_FILTERS);
  const [downloading, setDownloading] = useState(false);

  function handleDownload() {
    setDownloading(true);
    try {
      const json = serializeRows(rows);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cave84-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Section title="Backup">
      <div className="space-y-3">
        <p className="text-sm text-text-secondary">
          Download a full JSON export of all bottles across every cellar.
          {!isLoading && (
            <span className="text-text-muted"> ({rows.length} bottles)</span>
          )}
        </p>
        <button
          type="button"
          onClick={handleDownload}
          disabled={isLoading || downloading}
          className="flex items-center gap-2 px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-gold/40 hover:text-gold transition-colors disabled:opacity-50"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {isLoading ? 'Loading…' : downloading ? 'Preparing…' : 'Download backup.json'}
        </button>
        <p className="text-xs text-text-muted">
          Bottles are never deleted from Firestore — this export is an additional safety net.
        </p>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Batch Operations section
// ---------------------------------------------------------------------------

function BatchSection() {
  const ops = [
    {
      label: 'Refresh Market Prices',
      description: 'Run the price refresh wizard to pull latest valuations from auction records.',
      to: '/prices/refresh',
      icon: '💹',
    },
    {
      label: 'Import from CellarTracker',
      description: 'Bulk-import bottles via CellarTracker CSV export.',
      to: '/settings/import',
      icon: '📥',
    },
    {
      label: 'Analytics',
      description: 'View cellar analytics, geographic distribution, drinking window heatmap.',
      to: '/analytics',
      icon: '📊',
    },
    {
      label: 'Vintage Charts',
      description: 'Browse and update regional vintage quality ratings.',
      to: '/vintage-charts',
      icon: '📅',
    },
  ];

  return (
    <>
      <Section title="Operations">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ops.map((op) => (
            <Link
              key={op.to}
              to={op.to}
              className="flex items-start gap-3 p-3 rounded-md border border-border-subtle bg-surface-0 hover:border-gold/40 hover:bg-surface-2 transition-colors group"
            >
              <span className="text-xl mt-0.5 flex-shrink-0">{op.icon}</span>
              <div>
                <p className="text-sm font-medium text-text-secondary group-hover:text-text-primary transition-colors">
                  {op.label}
                </p>
                <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{op.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section title="Invoice import">
        <p className="text-xs text-text-muted">
          Supplier invoice imports (Munich Wine Company, Tomer Gal, …) are in the{' '}
          <Link to="/import" className="text-gold hover:underline">Import</Link> menu.
        </p>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Wine Regions section
// ---------------------------------------------------------------------------

// The 20 built-in regions (same as VintageChartsPage REGIONS constant)
const BUILTIN_REGIONS = [
  { id: 'Burgundy_Red',   label: 'Burgundy Red',        sub: 'Côte de Nuits · Côte de Beaune' },
  { id: 'Burgundy_White', label: 'Burgundy White',      sub: 'Côte de Beaune Blanc' },
  { id: 'Bordeaux_Left',  label: 'Bordeaux Left Bank',  sub: 'Médoc · Pessac-Léognan' },
  { id: 'Bordeaux_Right', label: 'Bordeaux Right Bank', sub: 'Saint-Émilion · Pomerol' },
  { id: 'Sauternes',      label: 'Sauternes',           sub: 'Barsac · Sauternes' },
  { id: 'Rhone_North',    label: 'Rhône North',         sub: 'Côte-Rôtie · Hermitage · Cornas' },
  { id: 'Rhone_South',    label: 'Rhône South',         sub: 'Châteauneuf-du-Pape · Gigondas' },
  { id: 'Champagne',      label: 'Champagne',           sub: 'Vintage years only' },
  { id: 'Barolo',         label: 'Barolo',              sub: 'Piedmont' },
  { id: 'Brunello',       label: 'Brunello',            sub: 'Tuscany · vintage years only' },
  { id: 'Bolgheri',       label: 'Bolgheri',            sub: 'Super-Tuscan' },
  { id: 'Loire',          label: 'Loire',               sub: 'Sancerre · Vouvray · Chinon' },
  { id: 'Alsace',         label: 'Alsace',              sub: 'Riesling · Gewürztraminer' },
  { id: 'Mosel_Rheingau', label: 'Mosel / Rheingau',    sub: 'Riesling' },
  { id: 'Rioja',           label: 'Rioja',               sub: 'Tempranillo · Alta · Alavesa' },
  { id: 'Ribera_del_Duero',label: 'Ribera del Duero',    sub: 'Tempranillo (Tinto Fino)' },
  { id: 'Priorat',         label: 'Priorat',             sub: 'Garnacha · Cariñena · llicorella' },
  { id: 'Tokaji',          label: 'Tokaji',              sub: 'Aszú · Eszencia' },
  { id: 'Nahe',            label: 'Nahe',                sub: 'Riesling' },
  { id: 'Amarone',         label: 'Amarone',             sub: 'Valpolicella Classico' },
];

function WineRegionsSection() {
  const { data: customRegions = [], isLoading } = useCustomWineRegions();
  const addRegion    = useAddCustomWineRegion();
  const deleteRegion = useDeleteCustomWineRegion();
  const slugify      = useSlugify();

  const [label,    setLabel]    = useState('');
  const [sub,      setSub]      = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error,    setError]    = useState('');

  async function handleAdd() {
    setError('');
    const trimmedLabel = label.trim();
    if (!trimmedLabel) { setError('Region name is required.'); return; }
    const id = slugify(trimmedLabel);
    if (!id) { setError('Invalid region name.'); return; }
    // Check for collision with built-in regions
    if (BUILTIN_REGIONS.find((r) => r.id === id || r.label.toLowerCase() === trimmedLabel.toLowerCase())) {
      setError('A built-in region with this name already exists.');
      return;
    }
    try {
      await addRegion.mutateAsync({ id, label: trimmedLabel, sub: sub.trim() });
      setLabel('');
      setSub('');
      setShowForm(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const inputCls = 'w-full px-3 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold';

  return (
    <Section title="Wine Regions">
      <div className="space-y-4">
        <p className="text-xs text-text-muted leading-relaxed">
          The Vintage Charts tracks these regions. The 20 built-in regions cannot be removed.
          You can add custom regions (e.g. Napa Valley, Barossa Valley) for your own vintage tracking.
        </p>

        {/* Built-in regions */}
        <div>
          <p className="text-xs text-text-muted mb-2 uppercase tracking-wide">Built-in ({BUILTIN_REGIONS.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {BUILTIN_REGIONS.map((r) => (
              <div key={r.id} className="flex items-center gap-2 px-3 py-2 rounded-md border border-border-subtle bg-surface-0">
                <span className="text-sm text-text-secondary font-medium flex-1 truncate">{r.label}</span>
                <span className="text-xs text-text-muted truncate max-w-[120px]">{r.sub}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Custom regions */}
        {(isLoading || customRegions.length > 0) && (
          <div>
            <p className="text-xs text-text-muted mb-2 uppercase tracking-wide">Custom ({isLoading ? '…' : customRegions.length})</p>
            {isLoading ? (
              <p className="text-xs text-text-muted animate-pulse">Loading…</p>
            ) : (
              <div className="space-y-1.5">
                {customRegions.map((r: CustomWineRegion) => (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-border-subtle bg-surface-0 group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-text-secondary font-medium">{r.label}</span>
                      {r.sub && <span className="text-xs text-text-muted ml-2">{r.sub}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => void deleteRegion.mutateAsync(r.id)}
                      disabled={deleteRegion.isPending}
                      className="opacity-0 group-hover:opacity-100 text-xs text-text-muted hover:text-red-400 transition-all disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add form */}
        {showForm ? (
          <div className="rounded-md border border-border-medium bg-surface-0 p-4 space-y-3">
            <p className="text-sm font-medium text-text-primary">Add Custom Region</p>
            <div>
              <span className="block text-xs text-text-muted mb-1">Region Name *</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Priorat"
                className={inputCls}
              />
              {label.trim() && (
                <p className="text-xs text-text-muted mt-1">ID will be: <span className="mono">{slugify(label)}</span></p>
              )}
            </div>
            <div>
              <span className="block text-xs text-text-muted mb-1">Sub-label (optional)</span>
              <input
                type="text"
                value={sub}
                onChange={(e) => setSub(e.target.value)}
                placeholder="e.g. Catalonia · Garnacha"
                className={inputCls}
              />
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setShowForm(false); setLabel(''); setSub(''); setError(''); }}
                className="px-3 py-1.5 rounded-md border border-border-medium text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={addRegion.isPending || !label.trim()}
                className="px-3 py-1.5 rounded-md bg-gold text-base text-xs font-medium hover:bg-gold/90 disabled:opacity-40 transition-colors"
              >
                {addRegion.isPending ? 'Adding…' : 'Add Region'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-border-medium text-sm text-text-secondary hover:border-gold/40 hover:text-gold transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Custom Region
          </button>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// About section
// ---------------------------------------------------------------------------

function AboutSection() {
  return (
    <Section title="About Cave84">
      <div className="space-y-2 text-sm text-text-secondary">
        <div className="flex justify-between items-center py-1 border-b border-border-subtle">
          <span className="text-text-muted text-xs">Application</span>
          <span className="font-medium">Cave84</span>
        </div>
        <div className="flex justify-between items-center py-1 border-b border-border-subtle">
          <span className="text-text-muted text-xs">Domain</span>
          <span className="mono text-xs">wine.bar84.com</span>
        </div>
        <div className="flex justify-between items-center py-1 border-b border-border-subtle">
          <span className="text-text-muted text-xs">Firebase project</span>
          <span className="mono text-xs">cave84-4ee22</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-text-muted text-xs">Stack</span>
          <span className="text-xs">React 19 · TypeScript · Firebase · Cloudinary</span>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const userDoc = useAuthStore((s) => s.userDoc);

  if (!userDoc || userDoc.role !== 'owner') {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <p className="text-text-muted text-sm">Settings is owner-only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3">
        <h1 className="text-base font-semibold text-text-primary">Settings</h1>
        <p className="text-xs text-text-muted">Profile, access, backup, and operations</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        <ProfileSection user={userDoc} />
        <AccessSection />
        <BackupSection />
        <BatchSection />
        <WineRegionsSection />
        <AboutSection />
      </div>
    </div>
  );
}
